import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { useApi, type ComboboxItemData } from '@shopexperts/shared';
import type { City } from '@shopexperts/legacy-api';

const PAGE_SIZE = 50;
const STALE_MS = 30 * 60 * 1000;

export function cityLabel(city: Pick<City, 'name' | 'region'>): string {
    return city.region ? `${city.name} · ${city.region}` : city.name;
}

export function cityOption(city: City): ComboboxItemData {
    return { value: String(city.id), label: cityLabel(city) };
}

/**
 * Pages through a country's cities, largest first, narrowed by a word-prefix search.
 * Pages are concatenated so a picker can append rows as the user scrolls.
 */
export function useCountryCitySearch(
    country: MaybeRefOrGetter<string | null | undefined>,
    query?: MaybeRefOrGetter<string | null | undefined>,
) {
    const api = useApi();
    const countryCode = computed(() => (toValue(country) ?? '').trim().toUpperCase());
    const search = computed(() => (toValue(query) ?? '').trim());
    const enabled = computed(() => countryCode.value.length === 2);

    const result = useInfiniteQuery({
        queryKey: computed(() => ['countries', countryCode.value, 'cities', search.value]),
        queryFn: ({ pageParam }) => api.cities.getCountryCities({
            params: { country: countryCode.value },
            query: search.value ? { q: search.value, per_page: PAGE_SIZE } : { per_page: PAGE_SIZE },
            page: pageParam,
        }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => (lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined),
        enabled,
        staleTime: STALE_MS,
        refetchOnWindowFocus: false,
    });

    const cities = computed<City[]>(() => (enabled.value ? result.data.value?.pages.flatMap((page) => page.data) ?? [] : []));
    const total = computed(() => result.data.value?.pages[0]?.pagination.total ?? 0);

    return {
        cities,
        total,
        isFetching: result.isFetching,
        hasMore: computed(() => Boolean(result.hasNextPage.value)),
        fetchNextPage: () => {
            if (result.hasNextPage.value && !result.isFetchingNextPage.value) void result.fetchNextPage();
        },
        error: result.error,
        enabled,
    };
}

/** Resolves GeoNames ids to cities across countries, for labelling stored ids. */
export function useCitiesByIds(ids: MaybeRefOrGetter<readonly (number | string)[]>) {
    const api = useApi();
    const normalized = computed(() => [...new Set(toValue(ids).map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b));
    const enabled = computed(() => normalized.value.length > 0);

    const { data, isFetching, error } = useQuery({
        queryKey: computed(() => ['cities', 'by-ids', normalized.value.join(',')]),
        queryFn: () => api.cities.getCitiesByIds({ query: { ids: normalized.value.join(',') } }),
        enabled,
        staleTime: STALE_MS,
        refetchOnWindowFocus: false,
    });

    const byId = computed(() => new Map((data.value ?? []).map((city) => [city.id, city])));

    return { cities: computed(() => data.value ?? []), byId, isFetching, error };
}
